import cloneDeep from 'clone-deep'

import { IMcrAdminView, IMcrConfig } from './types'

/**
 * Loan status to RMLA application disposition.
 *
 * Every row already resolves to something — an explicit mapping here, else the HMDA action-taken
 * mapping the tenant maintains for the same question, else In Process. The table shows that
 * resolved value next to the selector so an unset row reads as "inherited", not "unanswered",
 * which is the difference between a customer mapping five statuses and mapping twenty.
 */
export const McrDispositions = ({
  view,
  config,
  onChange,
}: {
  view: IMcrAdminView
  config: IMcrConfig
  onChange: (next: IMcrConfig) => void
}) => {
  const statusKeys = Object.keys(view.loanStatusLabels || {})

  const set = (statusKey: string, value: string) => {
    const next = cloneDeep(config)
    if (!value) delete next.statusDispositions[statusKey]
    else next.statusDispositions[statusKey] = value
    onChange(next)
  }

  return (
    <div>
      <p className="mb-3 text-[12px] text-gray-500">
        Which Section I line a loan lands on when it reaches each status. Closings are counted from the
        fund date rather than a status, so Loan Originated here only affects whether a file still counts
        as an open application.
      </p>

      <table className="w-full text-sm">
        <thead className="uppercase text-xs bg-gray-50">
          <tr>
            {['No', 'Loan Status', 'MCR Disposition', 'Resolves To'].map((v) => (
              <th scope="col" className="p-3 border" key={`header-${v}`}>
                {v}
              </th>
            ))}
          </tr>
        </thead>
        <tbody>
          {statusKeys.map((statusKey, index) => {
            const explicit = config.statusDispositions[statusKey] || ''
            const effective = view.effectiveDispositions[statusKey] || ''
            const inheritedFromHmda = !explicit && !!view.hmdaActionTaken[statusKey]
            return (
              <tr key={statusKey}>
                <td className="p-2 border w-12 text-center">{index + 1}</td>
                <td className="px-2 border">{view.loanStatusLabels[statusKey]}</td>
                <td className="px-2 py-1 border w-72">
                  <select
                    className="block rounded py-1.5 px-2 w-full text-sm text-gray-900 bg-transparent border border-gray-200 appearance-none focus:outline-none focus:ring-0 focus:border-gray-200"
                    value={explicit}
                    onChange={(e) => set(statusKey, e.target.value)}
                  >
                    <option value="">- Inherit -</option>
                    {Object.keys(view.dispositionOptions).map((optKey) => (
                      <option key={optKey} value={optKey}>
                        {view.dispositionOptions[optKey]}
                      </option>
                    ))}
                  </select>
                </td>
                <td className="px-2 border text-[12px] text-gray-500">
                  {view.dispositionOptions[effective] || '-'}
                  {!explicit && (
                    <span className="ml-1 text-gray-400">
                      ({inheritedFromHmda ? 'from HMDA Action Taken' : 'default'})
                    </span>
                  )}
                </td>
              </tr>
            )
          })}
        </tbody>
      </table>
    </div>
  )
}
